/**
 * @file 牌与实例 id 的映射原语。
 *
 * ## id ↔ 牌面 的公开契约
 *
 * `TileId` 取值范围 `0..107`，映射完全确定且公开：
 * - `kindIndex = floor(id / 4)`（0..26，对应 27 种牌面）
 * - `suitIndex = floor(kindIndex / 9)`（0=wan 1=tong 2=tiao）
 * - `rank = (kindIndex % 9) + 1`
 * - 同一牌面 4 张连续 id：`kindIndex * 4 + copy`（`copy` ∈ 0..3）
 *
 * 因为映射公开，客户端拿到 id 即知牌面；**信息隔离绝不能依赖「id 看起来不透明」**。
 * 见 `docs/architecture/00-overview.md` §6 与 ADR-004。
 */

import type { Rank, Suit, Tile, TileId, TileKind } from './types';
import { SUITS, TILE_KIND_COUNT } from './types';
import { InvariantViolationError } from './errors';

/** id → 牌面种类序号（0..26）。越界抛 {@link InvariantViolationError}。 */
export function kindIndexFromTileId(id: TileId): number {
  if (!Number.isInteger(id) || id < 0 || id >= TILE_KIND_COUNT * 4) {
    throw new InvariantViolationError(`TileId 越界: ${id}`, { id });
  }
  return Math.floor(id / 4);
}

/** 牌面种类序号 → 花色序号（0=wan 1=tong 2=tiao）。 */
export function suitIndexFromKindIndex(kindIndex: number): number {
  return Math.floor(kindIndex / 9);
}

/** 牌面种类序号 → 点数 1..9。 */
export function rankFromKindIndex(kindIndex: number): Rank {
  return ((kindIndex % 9) + 1) as Rank;
}

/** 由花色序号 + 点数构造牌面种类字符串。 */
export function tileKindFromSuitRank(suitIndex: number, rank: Rank): TileKind {
  return `${SUITS[suitIndex]}${rank}` as TileKind;
}

/**
 * 牌面种类序号（0..26） → 牌面种类。
 *
 * 与 {@link kindOfTileId} 的区别：入参是**种类序号**而非实例 id。
 * `win.ts` / `scoring.ts` 内部一律以 27 维计数向量运算，产出的都是种类序号，
 * 回到契约层（`TileKind` 字符串）必须经这一处转换——集中在这里是为了避免
 * 各处各写一遍 `SUITS[floor(k/9)] + (k%9+1)`。
 */
export function kindFromKindIndex(kindIndex: number): TileKind {
  return tileKindFromSuitRank(suitIndexFromKindIndex(kindIndex), rankFromKindIndex(kindIndex));
}

/** 花色 → 花色序号（0=wan 1=tong 2=tiao）。供 win.ts / scoring.ts 使用。 */
export function suitToIndex(suit: Suit): number {
  return SUITS.indexOf(suit);
}

/** 牌面种类 → 种类序号（0..26）。供 win.ts / scoring.ts 使用。 */
export function keyOfKind(kind: TileKind): number {
  const { suitIndex, rank } = parseTileKind(kind);
  return suitIndex * 9 + (rank - 1);
}

/** 牌面种类 → 点数 1..9。供 scoring.ts 使用。 */
export function rankOfKind(kind: TileKind): number {
  return parseTileKind(kind).rank;
}

/** 解析牌面种类字符串为 `[花色序号, 点数]`。 */
export function parseTileKind(kind: TileKind): { suitIndex: number; rank: Rank } {
  const suit = kind.slice(0, -1) as Suit;
  const suitIndex = SUITS.indexOf(suit);
  const rank = Number(kind.slice(-1)) as Rank;
  if (suit === undefined || suitIndex < 0 || rank < 1 || rank > 9) {
    throw new InvariantViolationError(`非法的 TileKind: ${kind}`, { kind });
  }
  return { suitIndex, rank };
}

/** 由牌实例 id 推导牌面种类。 */
export function kindOfTileId(id: TileId): TileKind {
  const kindIndex = kindIndexFromTileId(id);
  return tileKindFromSuitRank(suitIndexFromKindIndex(kindIndex), rankFromKindIndex(kindIndex));
}

/** 取某牌面对应的 4 个实例 id（升序）。 */
export function idsOfKind(kind: TileKind): readonly TileId[] {
  const { suitIndex, rank } = parseTileKind(kind);
  const kindIndex = suitIndex * 9 + (rank - 1);
  return [kindIndex * 4, kindIndex * 4 + 1, kindIndex * 4 + 2, kindIndex * 4 + 3];
}

/** 由牌实例 id 构造富信息 `Tile`（仅供 AI 评估与 UI 渲染）。 */
export function tileOf(id: TileId): Tile {
  const kindIndex = kindIndexFromTileId(id);
  const suitIndex = suitIndexFromKindIndex(kindIndex);
  const rank = rankFromKindIndex(kindIndex);
  const suit = SUITS[suitIndex]!;
  return { id, suit, rank, kind: tileKindFromSuitRank(suitIndex, rank) };
}

/** 生成一副未洗的标准牌（id 0..107 升序）。洗牌由 `createGame()` 用注入 PRNG 完成。 */
export function createOrderedDeck(): readonly TileId[] {
  const deck: TileId[] = [];
  for (let id = 0; id < TILE_KIND_COUNT * 4; id++) deck.push(id);
  return deck;
}
